import React from 'react';
import PropTypes from 'prop-types';
import {action, computed, observable} from 'mobx';
import {inject, observer} from 'mobx-react';
import {Link, routerShape, withRouter} from 'react-router';

import autobind from 'autobind-decorator';
import {withI18n, withLocalStorage} from 'spa/Components/Context';
import {cn} from 'spa/css';
import * as querystring from 'qs';

import BasketItemsList from './BasketItemsList';
import BasketItemLoading from './BasketItemLoading';
import Button from 'spa/Components/Button';
import EditBasketItem from './EditBasketItem';
import ProgressiveImage from 'spa/Components/ProgressiveImage';
import SidePanelHeader from 'spa/Components/SidePanel/SidePanelHeader';
import Tooltip from 'spa/Components/Tooltip';

import './basket.less';

import alert from 'spa/images/icons/alert-36.svg';
import back from 'spa/images/icons/back-dark-18.svg';

@withRouter
@inject(({
  store: {
    notifications,
    selection,
    tracker,
  },
}) => (
  {
    notifications,
    selection,
    tracker,
  }
))
@observer
@withI18n
@withLocalStorage
class Basket extends React.Component {
  static propTypes = {
    booking: PropTypes.shape({
      activeViewTreatment: PropTypes.object,
      address: PropTypes.object.isRequired,
      addTreatmentActive: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
      addTreatmentCategoryStep: PropTypes.bool,
      addTreatmentTreatmentsStep: PropTypes.bool,
      addTreatmentSelectedCategory: PropTypes.string,
      basket: PropTypes.object.isRequired,
      selection: PropTypes.object.isRequired,
    }),
    flowType: PropTypes.oneOf(['booking', 'therapists', 'treatments']),
    notifications: PropTypes.shape({
      add: PropTypes.func.isRequired,
    }).isRequired,
    noTopPadding: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    router: routerShape.isRequired,
    selectedTherapist: PropTypes.string,
    selection: PropTypes.object,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
  }

  static defaultProps = {
    noTopPadding: false,
  }

  UNSAFE_componentWillMount() {
    const {localStorageHelper} = this.context;
    const {booking, booking: {address, basket}} = this.props;

    if (window && window.localStorage && localStorageHelper) {
      const storageBasketItems = localStorageHelper.getItem('basketItems');
      const storageBasketWorkerCount = localStorageHelper.getItem('basketWorkerCount');

      if (storageBasketItems) {
        basket.basketItems = storageBasketItems;
      }

      if (storageBasketWorkerCount) {
        basket.workerCount = storageBasketWorkerCount;
      }
    }

    booking.address.load(booking.basket.basketPlaceId);

    this.validateBasket();
  }

  componentDidMount() {
    this.showCouplesCombineTooltip = true;
  }

  @observable basketValidation;
  @observable currentEditItem;
  @observable currentEditItemPosition;
  @observable showCouplesCombineTooltip = false;

  @autobind
  @action('validateBasket - Basket')
  validateBasket() {
    const {booking: {basket, basket: {basketServiceArea}}} = this.props;

    basket.validateBasket.put(`serviceArea=${basketServiceArea}`, basket.basketInfo)
      .then((response) => {
        this.onBasketValidateSuccess(response);
      }, (error) => {
        this.onBasketValidateFailure();
      });
  }

  @computed get isLoadingValidation() {
    const {booking: {basket}} = this.props;

    return basket.validateBasket.isLoading;
  }

  @autobind
  @action('onBasketValidateSuccess - Basket')
  onBasketValidateSuccess(response) {
    const {booking: {basket, selection}} = this.props;

    basket.validatedBasket = response;

    selection.basketValidated = true;
  }

  @autobind
  @action('onBasketValidateFailure - Basket')
  onBasketValidateFailure(error) {
    const {i18n} = this.context;
    const {booking: {basket}, notifications} = this.props;

    basket.reset();

    notifications.add({
      id: 'basket-error',
      type: 'error',
      title: i18n._x('We\'re sorry, an error occurred. Please refresh the page.', 'Basket'),
    });
  }

  @autobind
  @action('onClickAddTreatment - Basket')
  onClickAddTreatment(event) {
    const {
      booking,
      flowType,
      onClose,
      router,
    } = this.props;

    if (flowType === 'booking') {
      booking.addTreatmentActive = true;
      booking.addTreatmentCategoryStep = true;

      onClose();
    } else if (flowType === 'therapists') {
      const {i18n} = this.context;
      const {selectedTherapist} = this.props;
      const {selection: {address_id, place_id}} = booking;

      const queryObject = {
        address_id,
        addressConfirmed: true,
        addTreatmentActive: true,
        addTreatmentCategoryStep: true,
        basketConfirmed: true,
        place_id,
        therapist: selectedTherapist,
        therapistConfirmed: true,
      };

      router.push(`/${i18n.lc}/booking/?${querystring.stringify(queryObject)}`);
    } else if (flowType === 'treatments') {
      const {i18n} = this.context;
      const {selection: {address_id, place_id}} = booking;

      const queryObject = {
        address_id,
        addressConfirmed: true,
        addTreatmentActive: true,
        addTreatmentCategoryStep: true,
        basketConfirmed: true,
        place_id,
      };

      router.push(`/${i18n.lc}/booking/?${querystring.stringify(queryObject)}`);
    } else {
      const {i18n} = this.context;

      this.props.selection.place_id = null;

      this.props.booking.address.load(booking.basket.basketPlaceId);

      const queryObject = {
        addressConfirmed: true,
        addTreatmentActive: true,
        addTreatmentCategoryStep: true,
        basketConfirmed: true,
        place_id: booking.basket.basketPlaceId,
      };

      router.push(`/${i18n.lc}/booking/?${querystring.stringify(queryObject)}`);
    }
  }

  @autobind
  @action('onClickConfirmBasket - Basket')
  onClickConfirmBasket(event) {
    const {
      booking,
      booking: {selection},
      flowType,
      onClose,
      router,
    } = this.props;

    if (flowType === 'booking') {
      selection.basketConfirmed = true;
      booking.addTreatmentSelectedCategory = null;
      booking.addTreatmentCategoryStep = false;
      booking.addTreatmentTreatmentsStep = false;
    } else if (flowType === 'therapists') {
      const {i18n} = this.context;
      const {selectedTherapist} = this.props;
      const {address_id, place_id} = selection;

      const queryObject = {
        address_id,
        addressConfirmed: true,
        basketConfirmed: true,
        place_id,
        therapist: selectedTherapist,
        therapistConfirmed: true,
      };

      router.push(`/${i18n.lc}/booking/?${querystring.stringify(queryObject)}`);
    } else if (flowType === 'treatments') {
      const {i18n} = this.context;
      const {address_id, place_id} = selection;

      const queryObject = {
        address_id,
        addressConfirmed: true,
        basketConfirmed: true,
        place_id,
      };

      router.push(`/${i18n.lc}/booking/?${querystring.stringify(queryObject)}`);
    } else {
      const {i18n} = this.context;

      this.props.selection.place_id = null;

      this.props.booking.address.load(booking.basket.basketPlaceId);

      const queryObject = {
        addressConfirmed: true,
        basketConfirmed: true,
        place_id: booking.basket.basketPlaceId,
      };

      router.push(`/${i18n.lc}/booking/?${querystring.stringify(queryObject)}`);
    }

    onClose();
  }

  @autobind
  @action('onClickEdit - Basket')
  onClickEdit(event) {
    const {tracker} = this.props;
    const selectedItem = event.currentTarget.dataset.itemPosition;

    this.currentEditItem = this.items[selectedItem];
    this.currentEditItemPosition = selectedItem;

    tracker.track('Clicked edit item on basket screen', {
      category: 'Basket',
      label: this.currentEditItem.specialityName,
    });
  }

  @autobind
  @action('onClickRemoveItem - Basket')
  onClickRemoveItem(event) {
    this.props.booking.basket.basketItems.splice(this.currentEditItemPosition, 1);

    const {booking: {basket, selection}, tracker} = this.props;
    if (!basket) return null;

    basket.workerCount = (basket.workerCount === 2 && basket.basketItems.length !== 2) ? 1 : basket.workerCount;

    selection.basketItems = basket.basketItems;
    selection.basketWorkerCount = basket.workerCount;

    tracker.track('Clicked remove item on basket screen', {
      category: 'Basket',
      label: this.currentEditItem.specialityName,
    });

    this.currentEditItem = null;
    this.currentEditItemPosition = null;

    this.validateBasket();
  }

  @autobind
  @action('onClickCloseEdit - Basket')
  onClickCloseEdit(event) {
    const {tracker} = this.props;

    tracker.track('Clicked close edit panel on basket screen', {
      category: 'Basket',
      label: this.currentEditItem.specialityName,
    });

    this.currentEditItem = null;
    this.currentEditItemPosition = null;
  }

  @autobind
  @action('onClickSwitchCouples - Basket')
  onClickSwitchCouples(event) {
    const {booking: {basket}, tracker} = this.props;
    if (!basket) return null;

    basket.workerCount = basket.workerCount === 2 ? 1 : 2;

    this.props.booking.selection.basketWorkerCount = basket.workerCount;

    const trackingAction = basket.workerCount === 2 ?
      'Clicked switch to couples on basket screen' :
      'Clicked switch to back to back on basket screen';

    tracker.track(trackingAction, {
      category: 'Basket',
    });

    this.validateBasket();
  }

  @autobind
  @action('onClickViewTreatments - Basket')
  onClickViewTreatments(event) {
    const {onClose, tracker} = this.props;

    tracker.track('Clicked view treatments on empty basket', {
      category: 'Basket',
    });

    onClose();
  }

  @autobind
  @action('onCloseCouplesCombineToolTip - Basket')
  onCloseCouplesCombineToolTip(event) {
    this.showCouplesCombineTooltip = false;
  }

  /*
   * User edits the option for the selected treatment
   */
  @autobind
  @action('onEditOption - Basket')
  onEditOption(selectedOption) {
    this.props.booking.basket.basketItems[this.currentEditItemPosition].duration_id = selectedOption;

    this.currentEditItem = null;
    this.currentEditItemPosition = null;

    this.validateBasket();
  }

  @computed get basketError() {
    const {booking: {basket}} = this.props;
    if (!basket) return null;

    return basket.basketError;
  }

  @computed get canAddAdditionalTreatments() {
    const {booking: {basket}} = this.props;
    if (!basket) return null;

    const {canAddTreatments} = basket;

    return canAddTreatments;
  }

  @computed get canDoParallel() {
    const {booking: {basket}} = this.props;
    if (!basket) return null;

    const {canDoParallel} = basket;

    return canDoParallel;
  }

  @computed get items() {
    const {booking: {basket}} = this.props;
    if (!basket) return null;

    const {validatedBasket} = basket;

    return validatedBasket ? validatedBasket.basket : null;
  }

  @computed get parallelLabel() {
    const {booking: {basket}} = this.props;
    if (!basket) return null;

    const {parallelOptionLabel} = basket;

    return parallelOptionLabel;
  }

  @computed get formattedAddress() {
    const {booking: {address}} = this.props;

    if (!address.response || !address.response.formatted) {
      return null;
    }

    const address1 = address.response.formatted.address1 ? `${address.response.formatted.address1}, ` : '';

    return `${address1}${address.response.formatted.postcode}, ${address.response.formatted.city}`;
  }

  render() {
    const {i18n} = this.context;
    const {booking: {basket}, noTopPadding, onClose} = this.props;
    const {
      basketError,
      canAddAdditionalTreatments,
      canDoParallel,
      currentEditItem,
      formattedAddress,
      isLoadingValidation,
      items,
      onClickAddTreatment,
      onClickCloseEdit,
      onClickConfirmBasket,
      onClickEdit,
      onClickRemoveItem,
      onClickSwitchCouples,
      onClickViewTreatments,
      onEditOption,
      parallelLabel,
      showCouplesCombineTooltip,
    } = this;

    return (
      <div className={cn(
        'v-content v-content--expand basket__scroller',
        noTopPadding && 'basket__scroller--no-top-padding',
      )}>
        <SidePanelHeader
          icon={back}
          onClose={!currentEditItem ? onClose : onClickCloseEdit}
          noTopPadding={noTopPadding}
        />
        <div className="basket">
          {currentEditItem ? (
            <EditBasketItem
              itemInfo={currentEditItem}
              onClickClose={onClickCloseEdit}
              onClickRemove={onClickRemoveItem}
              onEditOption={onEditOption}
            />
          ) : (
            <div className="basket__container">
              {basket.basketItemsCount === 0 && !isLoadingValidation ? (
                <div>
                  <h1 className="basket__title">
                    {i18n._x('Your Basket', 'Basket panel title')}
                  </h1>
                  <div className="basket__empty-container">
                    <ProgressiveImage
                      className="basket__empty-img"
                      lowResImg="https://res.cloudinary.com/huxr6hrje/image/upload/f_auto,q_15,e_blur:2000/v1581085098/b2c-art/basket/empty_basket_1x.png"
                      highResImg="https://res.cloudinary.com/huxr6hrje/image/upload/f_auto/v1581085098/b2c-art/basket/empty_basket_1x.png"
                      highResImg2x="https://res.cloudinary.com/huxr6hrje/image/upload/f_auto/v1581085098/b2c-art/basket/empty_basket_2x.png"
                    />
                    <h3
                      className="basket__empty-title"
                      data-qa="basket-empty-message"
                    >
                      {i18n._x('Your basket is empty', 'Basket')}
                    </h3>
                  </div>
                  <div className="basket__fixed-control">
                    <Link
                      className="button button--block button--extra-rounded button--primary"
                      data-qa="basket-view-treatments-button"
                      onClick={onClickViewTreatments}
                      to={`/${i18n.lc}/treatments`}
                    >
                      {i18n._x('View treatments', 'Basket')}
                    </Link>
                  </div>
                </div>
              ) : (
                <div>
                  <h1 className="basket__title">
                    {i18n._x('Your Basket', 'Basket panel title')}
                  </h1>
                  <div className="basket__address-info-container">
                    <p className="basket__address-info">{i18n._x('Currently showing basket for: ', 'Basket panel')}</p>
                    <p className="basket__address-info">{formattedAddress}</p>
                  </div>
                  {isLoadingValidation ? (
                    <div className="basket__loading-container">
                      <BasketItemLoading />
                    </div>
                  ) : (
                    <div className="basket__list-container">
                      {basketError ? (
                        <div className="basket__warning-container">
                          <img src={alert} alt="" />
                          <h3 className="basket__warning-title">
                            {basketError.title}
                          </h3>
                          <p className="basket__warning-subtitle">
                            {basketError.subtitle}
                          </p>
                        </div>
                      ) : null}
                      {canDoParallel ? (
                        <div className="basket__warning-container">
                          <img src={alert} alt="" />
                          <p className="basket__warning-title">
                            {parallelLabel}
                          </p>
                          <button
                            className={cn(
                              'button button--block button--tiny-wide-short button--extra-rounded button--with-shadow',
                              basket.workerCount === 1 ? 'button--green-selected' : 'basket__change-couples-button',
                            )}
                            disabled={basket.workerCount === 1}
                            onClick={onClickSwitchCouples}
                          >
                            {i18n._x('Keep as one therapist', 'Basket screen')}
                          </button>
                          <button
                            className={cn(
                              'button button--block button--tiny-wide-short button--extra-rounded button--with-shadow',
                              basket.workerCount === 2 ? 'button--green-selected' : 'basket__change-couples-button',
                            )}
                            disabled={basket.workerCount === 2}
                            onClick={onClickSwitchCouples}
                          >
                            {i18n._x('Book a second therapist', 'Basket screen')}
                          </button>
                        </div>
                      ) : null}
                      {items ? (
                        <BasketItemsList
                          itemsList={items}
                          onClickEdit={onClickEdit}
                        />
                      ) : null}
                      <div className="basket__fixed-control">
                        {basket.workerCount === 2 ? (
                          <Tooltip
                            align="top"
                            clickOutsideToClose
                            containerClassName="basket__couples-tooltip"
                            hide={!showCouplesCombineTooltip}
                            onClose={this.onCloseCouplesCombineToolTip}
                            topOffset={30}
                            width={327}
                            zIndex={30}
                          >
                            <span>
                              {i18n._x('Just so you know, couples massages can’t be combined with other treatments for now.', 'Basket panel')}
                            </span>
                          </Tooltip>
                        ) : null}
                        <Button
                          caps={false}
                          className="button button--block button--extra-rounded button--normal button--with-disabled-lock basket__add-treatment-button"
                          disabled={!canAddAdditionalTreatments || isLoadingValidation}
                          label={i18n._x('Add another treatment', 'Basket selection')}
                          onClick={onClickAddTreatment}
                          theme="quaternary"
                        />
                        <Button
                          caps={false}
                          className="button button--block button--extra-rounded button--normal button--with-disabled-lock basket__proceed-button"
                          disabled={!!basketError || isLoadingValidation}
                          data-qa="basket-confirm-button"
                          label={i18n._x('Choose date & time', 'Basket selection')}
                          onClick={onClickConfirmBasket}
                          theme="primary"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default Basket;



// WEBPACK FOOTER //
// ./spa/Pages/Booking/Basket/index.jsx